import { AxiosError } from 'axios';
import { FC, MouseEvent, useEffect, useState } from 'react';
import UnsavedChangesModal from '../../../common/UnsavedChangesModal/UnsavedChangesModal';
import { showToast } from '../../../components/Toast/Toast';
import { ToastTypes } from '../../../components/Toast/Toast.types';

import { useAddUser } from '../../../services/user/user.service';
import { genericErrorShowToastOptions } from '../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import useNavigateWithToast from '../../../utils/useNavigateWithToast/useNavigateWithToast';
import UserForm from '../UserForm/UserForm';
import { UserFormErrors, UserFormValues } from '../UserForm/UserForm.types';

const AddUser: FC = () => {
  const navigateWithToast = useNavigateWithToast();
  const initialFormValues = {
    fullName: '', 
    loginId: '', 
    webAccess: undefined,
    security: undefined, 
  };
  const { mutate, isLoading } = useAddUser();

  const [formValues, setFormValues] = useState<UserFormValues>({
    fullName: '', 
    loginId: '', 
    webAccess: undefined, // backend enforces webAccess's value to be between 0 and 20 inclusive
    security: undefined, // backend enforces security to only be 1 or 2
  });

  const [formErrors, setFormErrors] = useState<UserFormErrors>({
    fullName: '', 
    loginId: '', 
    webAccess: '',
    security: '', 
  });

  const validateFormValues = () => {
    let error = false;
    let errorMessages: UserFormErrors = {
        fullName: '', 
        loginId: '', 
        webAccess: '',
        security: '', 
    };
    if ([null, undefined, ''].includes(formValues.fullName)  || 
        formValues.fullName.length < 1 || 
        formValues.fullName.length > 50) { 
            errorMessages.fullName = 'Full Name should be between 1 and 50 characters';
            error = true;
    } 
    if ([null, undefined, ''].includes(formValues.loginId)  || 
        formValues.loginId.length < 6 || 
        formValues.loginId.length > 50) { 
            errorMessages.loginId = 'LAN ID should be between 6 and 50 characters';
            error = true;
    } 
    if ([undefined, -1].includes(formValues.webAccess)) {
        errorMessages.webAccess = 'Web Access is required';
        error = true;
    }
    if ([undefined, -1].includes(formValues.security)) {
        errorMessages.security = 'Security is required';
        error = true;
    }
   
    setFormErrors({...formErrors, ...errorMessages})
    return error;
  };

  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const checkForUnsavedChanges = () => {
    if (
      initialFormValues.fullName === formValues.fullName &&
      initialFormValues.loginId === formValues.loginId &&
      initialFormValues.webAccess === formValues.webAccess &&
      initialFormValues.security === formValues.security
    ) {
      setUnsavedChanges(false);
      return;
    } else {
      setUnsavedChanges(true);
      return;
    }
  };

  useEffect(() => {
    checkForUnsavedChanges();
  }, [formValues]);

  const handleSave = (event: MouseEvent) => {
    event.preventDefault();
    const isError = validateFormValues();
    if (isError) return;
    setUnsavedChanges(false);
    mutate(
      {
        description: formValues.fullName as string,
        loginId: formValues.loginId as string,
        webAccess: formValues.webAccess as number,
        security: formValues.security as number,
        type: 'C',
        deleted: false,
      },
      {
        onSuccess: () => {
          navigateWithToast(`/tables/users`, {
            label: 'New user has been added successfully',
            id: 'userAddSuccess',
          });
        },
        onError: (e) => { 
            if ((e as AxiosError)?.message === 'Request failed with status code 409') {
                showToast({
                    label: 'User with the provided LAN ID already exists.', 
                    id: 'addUserIsError', 
                    toastType: ToastTypes.Error,
                })
                setUnsavedChanges(true);
            } else { 
                showToast(genericErrorShowToastOptions);
            }
            
        }
      }
    );
  };

  return (
    <>
      <UserForm
        formValues={formValues}
        formErrors={formErrors}
        setFormErrors={setFormErrors}
        setFormValues={setFormValues}
        headerTitle={'Add New User'}
        saveDisabled={false}
        handleSave={handleSave}
        isButtonLoading={isLoading}
      />
      <UnsavedChangesModal unsavedChangesExist={unsavedChanges} />
    </>
  );
};

export default AddUser;
